/* eslint-disable @nx/enforce-module-boundaries */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { eventUtil } from '@marriott/mi-ui-library';
import { MTag, MTagType } from '@marriott/mi-web-ui-kit';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { MPropertyReviewRating } from '../MPropertyReviewRating';
import { MPropertyLocationSummary } from '../MPropertyLocationSummary';
import { MPropertyPriceSection, DisplayMode } from '../MPropertyPriceSection';
import { SaveHotelButton } from '../MSaveHotelButton';
import { AI_SEARCH_CLICK_TRACK } from '../../utils/acdlUtils';
import * as Styled from './SmallPropertyCard.styles';

const MARRIOTT_CACHE_URL = 'https://cache.marriott.com';

const toAbsoluteImageUrl = (url?: string): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return `${url}?downsize=288px:*`;
  return `${MARRIOTT_CACHE_URL}${url.startsWith('/') ? '' : '/'}${url}?downsize=288px:*`
};

/** Matches shop map card: basicInformation.brand.photos[0].content[0].url */
const getBrandImageUrlFromProperty = (property?: Record<string, unknown>): string | undefined => {
  const basic = property?.['basicInformation'] as
    | { brand?: { photos?: Array<{ content?: Array<{ url?: string }> }> } }
    | undefined;
  const url = basic?.brand?.photos?.[0]?.content?.[0]?.url;
  return typeof url === 'string' && url.length > 0 ? url : undefined;
};

export interface RatingInfo {
  isMax: boolean;
  reviewUrl: string;
  reviewTitle: string;
  ratingValue: number;
  hasRating: boolean;
  reviewCountValue: number;
  reviewsLabel?: string;
}

type SmallPropertyCardImageState =
  | { status: 'prefetching' }
  | { status: 'ready'; src: string; variant: 'primary' | 'brand' }
  | { status: 'empty' };

export interface SmallPropertyCardProps {
  imageUrl?: string;
  /** Fallback when the property image fails to load; also read from `property` when omitted. */
  brandImageUrl?: string;
  /** Generic fallback image URL when both primary and brand images are missing. */
  fallbackImageUrl?: string;
  name: string;
  location?: number;
  /** Location information (city, state, country) to display in the card */
  locationSummary?: {
    city?: string;
    state?: string;
    country?: string;
  };
  /** Locale for distance formatting (e.g., 'en-US', 'de-DE'). Defaults to 'en-US'. */
  locale?: string;
  ratingInfo?: RatingInfo;
  description?: string;
  showDescription?: boolean;
  priceDisplay?: string;
  property?: Record<string, unknown>;
  edge?: Record<string, unknown>;
  onClick?: () => void;
  isBestMatch?: boolean;
  /** Property ID for the save functionality */
  propertyId?: string;
  /**
   * Display mode for the price section:
   * - 'cash': Show cash/currency price (default)
   * - 'points': Show points value
   * @default 'cash'
   */
  priceDisplayMode?: DisplayMode;
  /** Label to display when property is sold out */
  soldOutLabel?: string;
  /** Label to display when rate is unavailable (for special rate searches) */
  rateUnavailableLabel?: string;
  /** Whether the current search is a special rate search */
  isSpecialRateSearch?: boolean;
  /** Whether the current search is a flexible date search */
  isFlexibleDateSearch?: boolean;
  /** Property opening date - used to determine if hotel is unavailable */
  openingDate?: string;
  /** AMP request object for rate selection */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ampRequest?: any;
  /** Label for "Call to Book" edge case */
  callToBookLabel?: string;
  /** Label for "Opening Soon" edge case (e.g., "Opening") */
  openingSoonLabel?: string;
  /** Date format for opening soon (e.g., "MMMM YYYY") */
  openingSoonDateFormat?: string;
  /** Whether the property is bookable online */
  isBookable?: boolean;
  /** Label for "Max Occupancy" edge case */
  maxOccupancyLabel?: string;
  /** Label for "Adults Only" edge case */
  adultsOnlyLabel?: string;
  /** Whether the property is adults-only */
  isAdultsOnly?: boolean;
  /** Number of children in the search */
  childCount?: number;
  /** URL for the "View Rates" button in the fee modal */
  viewRatesURL?: string;
  /** Label for the "View Rates" button in the fee modal */
  viewRatesLabel?: string;
  /** Callback when the image is clicked (opens gallery). Falls back to onClick if not provided. */
  onImageClick?: () => void;
}

export const SmallPropertyCard: React.FC<SmallPropertyCardProps> = ({
  imageUrl,
  brandImageUrl,
  fallbackImageUrl,
  name,
  locationSummary,
  ratingInfo,
  description,
  showDescription = false,
  priceDisplay,
  property,
  edge,
  onClick,
  propertyId,
  priceDisplayMode = 'cash',
  soldOutLabel,
  rateUnavailableLabel,
  isSpecialRateSearch,
  isFlexibleDateSearch,
  openingDate,
  ampRequest,
  callToBookLabel,
  openingSoonLabel,
  openingSoonDateFormat,
  isBookable,
  maxOccupancyLabel,
  adultsOnlyLabel,
  isAdultsOnly,
  childCount,
  viewRatesURL,
  viewRatesLabel,
  onImageClick,
}) => {
  // Track if an interactive element inside the card was clicked to prevent card click
  const skipCardClickRef = useRef(false);

  const handleCardClick = useCallback(() => {
    if (skipCardClickRef.current) {
      skipCardClickRef.current = false;
      return;
    }
    onClick?.();
  }, [onClick]);

  const handlePropertyCardHover = (): void => {
    const propertyId = (property as { id?: string })?.id;
    if (!propertyId) return;
    eventUtil?.dispatch('propCardOver', { id: propertyId });
  };

  const handlePropertyCardUnhover = (): void => {
    eventUtil?.dispatch('propCardUnOver', null);
  };

  const resolvedBrandRaw = brandImageUrl ?? getBrandImageUrlFromProperty(property);
  const normalizedPrimary = useMemo(() => toAbsoluteImageUrl(imageUrl), [imageUrl]);
  const normalizedBrand = useMemo(() => toAbsoluteImageUrl(resolvedBrandRaw), [resolvedBrandRaw]);

  const [imageState, setImageState] = useState<SmallPropertyCardImageState>(() =>
    normalizedPrimary
      ? { status: 'prefetching' }
      : normalizedBrand
        ? { status: 'ready', src: normalizedBrand, variant: 'brand' }
        : { status: 'empty' }
  );

  const urlsRef = useRef({ primary: normalizedPrimary, brand: normalizedBrand });
  urlsRef.current = { primary: normalizedPrimary, brand: normalizedBrand };

  useEffect(() => {
    if (normalizedPrimary) {
      setImageState({ status: 'prefetching' });
    } else {
      setImageState(
        normalizedBrand ? { status: 'ready', src: normalizedBrand, variant: 'brand' } : { status: 'empty' }
      );
    }
  }, [normalizedPrimary, normalizedBrand]);

  const handlePrimaryPrefetchLoad = useCallback(() => {
    setImageState(prev => {
      if (prev.status !== 'prefetching') return prev;
      return { status: 'ready', src: urlsRef.current.primary, variant: 'primary' };
    });
  }, []);

  const handlePrimaryPrefetchError = useCallback(() => {
    setImageState(prev => {
      if (prev.status !== 'prefetching') return prev;
      const { brand } = urlsRef.current;
      if (brand) return { status: 'ready', src: brand, variant: 'brand' };
      return fallbackImageUrl
        ? { status: 'ready', src: fallbackImageUrl, variant: 'brand' as const }
        : { status: 'empty' };
    });
  }, [fallbackImageUrl]);

  const displaySrc = useMemo((): string => {
    if (imageState.status === 'empty') return '';
    if (imageState.status === 'prefetching') return normalizedPrimary;
    return imageState.src;
  }, [imageState, normalizedPrimary]);

  return (
    <Styled.CardWrapper
      $clickable={!!onClick}
      onClickCapture={e => {
        // If clicking on a button inside the card, skip the card click handler
        if ((e.target as HTMLElement).closest('button')) {
          skipCardClickRef.current = true;
        }
      }}
      onClick={handleCardClick}
      onMouseEnter={handlePropertyCardHover}
      onMouseLeave={handlePropertyCardUnhover}
      className={onClick ? 'custom_click_track' : undefined}
      data-custom_click_track_value={onClick ? AI_SEARCH_CLICK_TRACK.VIEW_RATES : undefined}
    >
      <Styled.ImageContainer
        onClick={(e) => {
          if (onImageClick) {
            e.stopPropagation();
            onImageClick();
          }
        }}
      >
        {displaySrc ? (
          <Styled.Image
            src={displaySrc}
            alt={name}
            loading="lazy"
            onLoad={handlePrimaryPrefetchLoad}
            onError={handlePrimaryPrefetchError}
          />
        ) : fallbackImageUrl ? (
          <Styled.Image src={fallbackImageUrl} alt={name} loading="lazy" />
        ) : null}
        <Styled.BestMatchBadge>
          <MTag
            type={MTagType.Default}
            aIcon={{ icon: 'icon-new', iconClass: 'icon-decorative', color: 'grey' }}
            aLabel={{ text: 'AI Match' }}
          />
        </Styled.BestMatchBadge>

        {propertyId && (
          <Styled.FavoriteSaveButtonWrapper
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <SaveHotelButton propertyID={propertyId} clickTrackValue={AI_SEARCH_CLICK_TRACK.FAVORITE_HOTEL} />
          </Styled.FavoriteSaveButtonWrapper>
        )}
      </Styled.ImageContainer>

      <Styled.ContentWrapper>
        <Styled.HotelDescriptionWrapper>
          <Styled.HotelName className="t-subtitle-s" title={name}>
            {name}
          </Styled.HotelName>

          <Styled.RatingDistanceRow>
            {ratingInfo && <MPropertyReviewRating ratingInfo={ratingInfo} />}
            {locationSummary && <MPropertyLocationSummary locationSummary={locationSummary} showLocationIcon={true} />}
          </Styled.RatingDistanceRow>
        </Styled.HotelDescriptionWrapper>

        {showDescription && description && (
          <Styled.DescriptionWrapper className="t-font-alt-xs">
            <MarkdownPreview source={description} />
          </Styled.DescriptionWrapper>
        )}

        {(priceDisplay || edge) && (
          <MPropertyPriceSection
            edge={edge}
            priceDisplay={priceDisplay}
            showDivider={false}
            displayMode={priceDisplayMode}
            soldOutLabel={soldOutLabel}
            rateUnavailableLabel={rateUnavailableLabel}
            isSpecialRateSearch={isSpecialRateSearch}
            isFlexibleDateSearch={isFlexibleDateSearch}
            openingDate={openingDate}
            ampRequest={ampRequest}
            callToBookLabel={callToBookLabel}
            openingSoonLabel={openingSoonLabel}
            openingSoonDateFormat={openingSoonDateFormat}
            isBookable={isBookable}
            maxOccupancyLabel={maxOccupancyLabel}
            adultsOnlyLabel={adultsOnlyLabel}
            isAdultsOnly={isAdultsOnly}
            childCount={childCount}
            viewRatesURL={viewRatesURL}
            viewRatesLabel={viewRatesLabel}
          />
        )}
      </Styled.ContentWrapper>
    </Styled.CardWrapper>
  );
};
